import {Box, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import DetailsLoading from "crqlar-react-common/dist/components/DetailsLoading";
import type {AggregationSurveyEvaluation}
    from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {Survey} from "crqlar-react-common/dist/models/survey/survey.model";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {matchesTitle} from "../../insights/kit/InsightsRailSearch";
import {isAnswerableQuestion, SurveyAnswerContext, surveyQuestionTitle} from "../shared/surveyAnswer.util";
import {evaluationBuckets, evaluationLabelWidth} from "./surveyEvaluation.util";
import SurveyEvaluationQuestionCard, {SurveyEvaluationViewMode} from "./SurveyEvaluationQuestionCard";

interface Props {
    survey: Survey
    evaluation?: AggregationSurveyEvaluation
    loading?: boolean
    ctx: SurveyAnswerContext
    currentCaption: string
    compareCaption: string
    /** Narrows the list to the questions whose title contains it; empty shows every question. */
    query?: string
}

function SurveyEvaluationQuestionList({
    survey,
    evaluation,
    loading,
    ctx,
    currentCaption,
    compareCaption,
    query = ''
}: Props) {
    const {t} = useTranslation()

    // One switch for the page: flipping a question to the list flips them all, as it did before.
    const [view, setView] = useState<SurveyEvaluationViewMode>('chart')

    const questions = useMemo(
        () => (survey.questions ?? []).filter(isAnswerableQuestion),
        [survey.questions]
    )

    const evaluationByQuestionId = useMemo(() => {
        const map = new Map<string, AggregationSurveyEvaluation['questions'][number]>()
        evaluation?.questions.forEach(entry => map.set(entry.questionId, entry))
        return map
    }, [evaluation])

    // Decided once for the whole tab: a card that picks its own column starts its bars somewhere
    // else than the card above it.
    const labelWidth = useMemo(
        () => evaluationLabelWidth(questions.flatMap(question => {
            const entry = question.id ? evaluationByQuestionId.get(question.id) : undefined

            return evaluationBuckets(question, ctx, entry?.valueCounts, entry?.compareValueCounts)
                .map(bucket => bucket.label)
        })),
        [questions, evaluationByQuestionId, ctx]
    )

    // A question keeps its number while the search hides the ones around it, so "4." is still the
    // fourth question of the survey.
    const shownQuestions = useMemo(
        () => questions
            .map((question, index) => ({question, number: index + 1}))
            .filter(({question}) => matchesTitle(query, surveyQuestionTitle(question, ctx))),
        [questions, query, ctx]
    )

    if (loading) return <DetailsLoading/>

    if (!questions.length) {
        return (
            <Typography variant={'body2'} color={'text.secondary'}>
                {t('This survey has no questions yet.')}
            </Typography>
        )
    }

    if (!shownQuestions.length) {
        return (
            <Typography variant={'body2'} color={'text.secondary'} sx={{py: 6, textAlign: 'center'}}>
                {t('No questions match your search.')}
            </Typography>
        )
    }

    return (
        <Stack gap={2}>
            {shownQuestions.map(({question, number}) => (
                <SurveyEvaluationQuestionCard
                    key={question.id ?? number}
                    question={question}
                    index={number}
                    evaluation={question.id ? evaluationByQuestionId.get(question.id) : undefined}
                    ctx={ctx}
                    currentCaption={currentCaption}
                    compareCaption={compareCaption}
                    view={view}
                    onViewChange={setView}
                    labelWidth={labelWidth}
                />
            ))}

            {/* Lets the last card scroll clear of the viewport bottom instead of sitting against it. */}
            <Box sx={{height: 400}}/>
        </Stack>
    )
}

export default SurveyEvaluationQuestionList;
